// Modello dati e serializzatore HTML per l'editor WYSIWYG della carta cartacea.
// Obiettivo: rappresentare il menu come una lista di BLOCCHI di testo con id stabili,
// applicare stili di gruppo (per ruolo) + override per singolo blocco, e serializzare
// tutto in HTML identico tra anteprima editor e PDF/stampa (WYSIWYG garantito).
import { dietIconsHtml } from './dietTags';

// Dimensioni A4 in px @96dpi (210mm larghezza). Coerenti col rendering PDF a 210mm.
export const A4_W = 794;
export const A4_H = 1123;
export const PX_PER_MM = A4_W / 210; // ~3.781
// Griglia di snap per il trascinamento (in px A4).
export const GRID = 8;
// Margine interno pagina (mm) e in px.
export const PAGE_MARGIN_MM = 24;
export const PAGE_PAD_PX = Math.round(PAGE_MARGIN_MM * PX_PER_MM);

// Font disponibili per i testi della carta (già caricati nel documento).
export const PAPER_FONT_OPTIONS = [
  { id: 'playfair', label: 'Playfair Display', family: "'Playfair Display', serif", weight: 700 },
  { id: 'garamond', label: 'EB Garamond', family: "'EB Garamond', serif", weight: 600 },
  { id: 'montserrat', label: 'Montserrat', family: "'Montserrat', sans-serif", weight: 600 },
  { id: 'anton', label: 'Anton', family: "'Anton', sans-serif", weight: 400 },
  { id: 'fredoka', label: 'Fredoka', family: "'Fredoka', sans-serif", weight: 600 },
  { id: 'crimson', label: 'Crimson Text', family: "'Crimson Text', serif", weight: 600 },
];
export const fontById = (id) => PAPER_FONT_OPTIONS.find((f) => f.id === id) || PAPER_FONT_OPTIONS[0];

// Ruoli tipografici (gruppi di stile). Ogni ruolo ha font/dimensione/colore di default.
export const PAPER_ROLES = ['menuTitle', 'category', 'subcategory', 'itemName', 'itemPrice', 'itemDesc'];

// Stili di gruppo di default, derivati dai colori del tema (accent/ink/muted).
export const defaultGroupStyles = (accent, ink, muted) => ({
  menuTitle: { fontId: 'playfair', sizePx: 26, color: ink },
  category: { fontId: 'playfair', sizePx: 22, color: accent },
  subcategory: { fontId: 'playfair', sizePx: 15, color: muted },
  itemName: { fontId: 'montserrat', sizePx: 15, color: ink },
  itemPrice: { fontId: 'montserrat', sizePx: 15, color: accent },
  itemDesc: { fontId: 'montserrat', sizePx: 12, color: muted },
});

// Unisce gli stili di gruppo salvati con i default (nel caso manchino ruoli/campi).
export const mergeGroupStyles = (saved, accent, ink, muted) => {
  const def = defaultGroupStyles(accent, ink, muted);
  const out = {};
  PAPER_ROLES.forEach((r) => { out[r] = { ...def[r], ...((saved && saved[r]) || {}) }; });
  return out;
};

// ---- Utility ----
export const escHtml = (s) => String(s == null ? '' : s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

export const hexToRgba = (hex, a) => {
  const h = String(hex || '').replace('#', '');
  const f = h.length === 3 ? h.split('').map((x) => x + x).join('') : h;
  const n = parseInt(f, 16);
  if (isNaN(n)) return `rgba(0,0,0,${a})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

const productData = (p) => ({
  name: p.name,
  price: p.price,
  description: p.description,
  variants: p.variants || [],
  diet_tags: p.diet_tags || [],
});

// Costruisce la lista di blocchi dal menu (categorie/sottocategorie/prodotti).
// Ogni blocco ha:
//  - id: stabile ('menuTitle' | 'cat:{id}' | 'sub:{id}' | 'item:{id}')
//  - role: 'menuTitle' | 'category' | 'subcategory' | 'item'
//  - sg: sottogruppo indivisibile per l'impaginazione (mai spezzato tra pagine)
//  - g: indice categoria
export const buildMenuBlocks = (cats, { menuTitle } = {}) => {
  const blocks = [{ id: 'menuTitle', role: 'menuTitle', sg: 'title', g: -1, title: menuTitle || 'MENU' }];
  (cats || []).forEach((cat, gi) => {
    const catSg = `${gi}:cat`;
    blocks.push({ id: `cat:${cat.id}`, role: 'category', sg: catSg, g: gi, title: cat.name });
    (cat.products || []).forEach((p) => blocks.push({ id: `item:${p.id}`, role: 'item', sg: catSg, g: gi, ...productData(p) }));
    (cat.subcategories || []).filter((s) => s.products?.length).forEach((s, si) => {
      const subSg = `${gi}:sub${si}`;
      blocks.push({ id: `sub:${s.id}`, role: 'subcategory', sg: subSg, g: gi, title: s.name });
      (s.products || []).forEach((p) => blocks.push({ id: `item:${p.id}`, role: 'item', sg: subSg, g: gi, ...productData(p) }));
    });
  });
  return blocks;
};

// Risolve lo stile effettivo di un ruolo unendo gruppo + override del blocco.
const styleFor = (role, groupStyles, ov) => {
  const base = (groupStyles && groupStyles[role]) || {};
  const fontId = (ov && ov.fontId) || base.fontId;
  const sizePx = (ov && ov.sizePx) || base.sizePx;
  const color = (ov && ov.color) || base.color;
  const fo = fontById(fontId);
  return { family: fo.family, weight: fo.weight, sizePx, color };
};

// Serializza il CONTENUTO di un blocco in HTML (senza wrapper di posizione).
// Usato SIA per l'anteprima interattiva SIA per il PDF/stampa.
export const renderBlockHtml = (block, { groupStyles, overrides = {}, accent, money }) => {
  const ov = overrides[block.id] || {};
  const money$ = money || ((v) => `${v}`);
  const dots = hexToRgba(accent, 0.5);

  if (block.role === 'menuTitle') {
    const s = styleFor('menuTitle', groupStyles, ov);
    return `<div style="text-align:center;margin:0 0 18px;">`
      + `<span style="font-family:${s.family};font-weight:${s.weight};font-size:${s.sizePx}px;letter-spacing:4px;text-transform:uppercase;color:${s.color};border-bottom:2px solid ${accent};display:inline-block;padding-bottom:8px;line-height:1.1;">${escHtml(block.title || 'MENU')}</span>`
      + `</div>`;
  }
  if (block.role === 'category') {
    const s = styleFor('category', groupStyles, ov);
    return `<h2 style="font-family:${s.family};font-weight:${s.weight};font-size:${s.sizePx}px;color:${s.color};margin:14px 0 6px;padding-bottom:5px;text-transform:uppercase;letter-spacing:.3px;border-bottom:1px solid ${hexToRgba(accent, 0.35)};line-height:1.15;">${escHtml(block.title)}</h2>`;
  }
  if (block.role === 'subcategory') {
    const s = styleFor('subcategory', groupStyles, ov);
    return `<h3 style="font-family:${s.family};font-weight:${s.weight};font-style:italic;font-size:${s.sizePx}px;color:${s.color};margin:10px 0 3px;line-height:1.2;">${escHtml(block.title)}</h3>`;
  }
  // role === 'item'
  const sn = styleFor('itemName', groupStyles, ov);
  const sp = styleFor('itemPrice', groupStyles, {});
  const sd = styleFor('itemDesc', groupStyles, {});
  const diet = block.diet_tags?.length ? '&nbsp;' + dietIconsHtml(block.diet_tags, 13, 3) : '';
  const variants = block.variants?.length
    ? `<div style="font-family:${sd.family};font-size:${Math.max(10, sd.sizePx - 1)}px;color:${sd.color};margin-top:2px;font-style:italic;">${block.variants.map((v) => `${escHtml(v.name)}${Number(v.price_delta) ? ` +${escHtml(money$(v.price_delta))}` : ''}`).join('  ·  ')}</div>`
    : '';
  return `<div style="margin:5px 0;">`
    + `<div style="display:flex;align-items:baseline;">`
    + `<span style="font-family:${sn.family};font-weight:${sn.weight};font-size:${sn.sizePx}px;color:${sn.color};line-height:1.25;">${escHtml(block.name)}${diet}</span>`
    + `<span style="flex:1 1 auto;border-bottom:1.5px dotted ${dots};margin:0 8px;transform:translateY(-5px);"></span>`
    + `<span style="font-family:${sp.family};font-weight:${sp.weight};font-size:${sp.sizePx}px;color:${sp.color};white-space:nowrap;">${escHtml(money$(block.price))}</span>`
    + `</div>`
    + (block.description ? `<div style="font-family:${sd.family};font-size:${sd.sizePx}px;color:${sd.color};margin-top:2px;max-width:92%;line-height:1.35;">${escHtml(block.description)}</div>` : '')
    + variants
    + `</div>`;
};

// CSS di sfondo di una pagina: foglio A4 BIANCO con eventuale grafica AI dietro,
// attenuata da un velo bianco per garantire la leggibilità del testo.
export const pageBgCss = (aiPageBg, whiteVeil = 0.86) => {
  if (aiPageBg) {
    return `background-color:#ffffff;background-image:linear-gradient(rgba(255,255,255,${whiteVeil}),rgba(255,255,255,${whiteVeil})),url('${aiPageBg}');background-size:cover;background-position:center;`;
  }
  return 'background-color:#ffffff;';
};

// ---- Impaginazione (misurazione DOM) ----
// Raggruppa i blocchi in unità indivisibili (per sg) e li distribuisce in pagine A4
// rispettando le interruzioni di pagina manuali (overrides[id].pageBreakBefore).
export const paginate = (blocks, heights, overrides = {}) => {
  const contentH = A4_H - PAGE_PAD_PX * 2;
  // Unità indivisibili per sg.
  const units = [];
  let uStart = 0;
  for (let i = 1; i <= blocks.length; i++) {
    if (i === blocks.length || blocks[i].sg !== blocks[uStart].sg) {
      units.push({ start: uStart, end: i - 1 });
      uStart = i;
    }
  }
  const unitH = (u) => { let h = 0; for (let i = u.start; i <= u.end; i++) h += heights[i] || 0; return h; };
  const pages = [[]];
  let pi = 0;
  let used = 0;
  const pushUnit = (u) => { for (let i = u.start; i <= u.end; i++) pages[pi].push(i); used += unitH(u); };
  const newPage = () => { pages.push([]); pi += 1; used = 0; };
  units.forEach((u) => {
    const h = unitH(u);
    const firstId = blocks[u.start].id;
    const forceBreak = !!(overrides[firstId] && overrides[firstId].pageBreakBefore);
    if (pages[pi].length > 0 && (forceBreak || used + h > contentH)) newPage();
    pushUnit(u);
  });
  return pages;
};

// ---- Layout a tela libera (posizionamento assoluto per pagina) ----
// Ogni blocco ha una posizione risolta { page, y } (px A4, relativa all'area contenuto).
// I blocchi non ancora spostati manualmente ricevono una posizione automatica calcolata
// dall'impaginazione a flusso; quelli con overrides[id].pos usano quella esplicita.
export const CONTENT_W = A4_W - PAGE_PAD_PX * 2;

export const computeAutoLayout = (blocks, heights, overrides = {}) => {
  const pages = paginate(blocks, heights, overrides);
  const auto = {};
  pages.forEach((pg, pi) => {
    let y = 0;
    pg.forEach((bi) => {
      auto[blocks[bi].id] = { page: pi, y };
      y += heights[bi] || 0;
    });
  });
  return { auto, pageCount: Math.max(1, pages.length) };
};

export const resolveLayout = (blocks, auto, overrides = {}) => {
  const out = {};
  blocks.forEach((b) => {
    const pos = overrides[b.id] && overrides[b.id].pos;
    out[b.id] = pos ? { page: pos.page || 0, y: pos.y || 0 } : (auto[b.id] || { page: 0, y: 0 });
  });
  return out;
};

// Serializza l'intero menu (multipagina) in HTML per PDF/stampa.
// layout = mappa id -> { page, y }; pageCount = numero pagine da renderizzare.
export const buildMenuHtml = (blocks, layout, pageCount, { groupStyles, overrides = {}, accent, money, aiPageBg }) => {
  const bg = pageBgCss(aiPageBg);
  const sections = [];
  for (let pi = 0; pi < pageCount; pi++) {
    const inner = blocks.map((b) => {
      const ov = overrides[b.id] || {};
      if (ov.hidden) return '';
      const pos = layout[b.id];
      if (!pos || (pos.page || 0) !== pi) return '';
      const content = renderBlockHtml(b, { groupStyles, overrides, accent, money });
      return `<div style="position:absolute;left:${PAGE_PAD_PX}px;top:${PAGE_PAD_PX + Math.round(pos.y || 0)}px;width:${CONTENT_W}px;">${content}</div>`;
    }).join('');
    sections.push(`<section class="menu-page" style="${bg}position:relative;width:210mm;height:297mm;box-sizing:border-box;overflow:hidden;">${inner}</section>`);
  }
  return sections.join('<div class="pb" style="page-break-before:always;height:0;overflow:hidden;"></div>');
};

// ---- Persistenza (localStorage per Step 1) ----
const STORE_KEY = (rid) => `vexel_paper_editor_${rid || 'default'}`;
export const loadEditorState = (rid) => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY(rid)) || 'null'); } catch (e) { return null; }
};
export const saveEditorState = (rid, state) => {
  try { localStorage.setItem(STORE_KEY(rid), JSON.stringify(state)); } catch (e) { /* noop */ }
};
export const clearEditorState = (rid) => {
  try { localStorage.removeItem(STORE_KEY(rid)); } catch (e) { /* noop */ }
};
